import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { MeetingHolidayApiService } from '../../../services/meeting-holiday-api.service';
import { CurrencyService } from '../../../../currency/currency.service';
import { CountryResponse } from '../../../../currency/models/currency.model';
import {
  MeetingHolidayRequest,
  MeetingHolidayResponse,
} from '../../../models/meeting.model';

@Component({
  selector: 'app-holidays-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './holidays-form.component.html',
})
export class HolidaysFormComponent implements OnInit {
  @Input() drawerMode = true;

  @Output() saved = new EventEmitter<MeetingHolidayResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  holidayId: number | null = null;
  private originalName = '';

  // Países por nombre en lugar de códigos técnicos (INC 27)
  countries: CountryResponse[] = [];

  constructor(
    private fb: FormBuilder,
    private api: MeetingHolidayApiService,
    private authService: AuthService,
    private currencyService: CurrencyService,
    private toast: ToastService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // En drawer mode el wrapper invoca reset()/loadHoliday() al abrir.
    this.currencyService.getCountries().subscribe({
      next: (countries) =>
        (this.countries = [...countries].sort((a, b) => a.name.localeCompare(b.name))),
      error: () => console.error('Error cargando países'),
    });
  }

  /** Código ISO del país (el backend expone alphabeticCode). */
  countryCodeOf(c: CountryResponse): string {
    return c.alphabeticCode || c.code || '';
  }

  get savedCountryNotInList(): string {
    const v: string = this.form?.get('countryCode')?.value || '';
    if (!v) return '';
    return this.countries.some((c) => this.countryCodeOf(c).toUpperCase() === v.toUpperCase())
      ? ''
      : v;
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  private initForm(): void {
    this.form = this.fb.group({
      holidayDate: ['', [Validators.required]],
      name: ['', [Validators.required, Validators.maxLength(150)]],
      countryCode: ['', [Validators.maxLength(10)]],
      isRecurring: [false],
      isActive: [true],
    });
  }

  reset(): void {
    this.isEditing = false;
    this.holidayId = null;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.initForm();
  }

  loadHoliday(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.holidayId = id;
    this.api.getById(id, this.businessId).subscribe({
      next: (item) => {
        this.form.patchValue({
          holidayDate: item.holidayDate,
          name: item.name,
          countryCode: item.countryCode || '',
          isRecurring: !!item.isRecurring,
          isActive: item.isActive ?? true,
        });
        this.originalName = item.name;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el feriado';
        this.loading = false;
      },
    });
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const body: MeetingHolidayRequest = {
      businessId: this.businessId,
      holidayDate: v.holidayDate,
      name: v.name.trim(),
      countryCode: v.countryCode?.trim() || undefined,
      isRecurring: !!v.isRecurring,
      isActive: !!v.isActive,
    };

    const req$ = this.isEditing && this.holidayId
      ? this.api.update(this.holidayId, this.businessId, body)
      : this.api.create(body);

    req$.subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) {
          this.toast.updated('Feriado',
            this.originalName !== v.name.trim()
              ? `Se actualizó "${this.originalName}" → "${v.name.trim()}".`
              : `Se actualizó "${v.name.trim()}" correctamente.`);
        } else {
          this.toast.created('Feriado', `Se creó "${v.name.trim()}" correctamente.`);
        }
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        this.error = err.error?.message || 'Error al guardar';
        this.toast.error({ title: 'Error', message: this.error });
      },
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Campo inválido';
  }
}
